import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Drawer,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Divider,
  LinearProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CategoryIcon from '@material-ui/icons/Category';
import DescriptionIcon from '@material-ui/icons/Description';

const MIN_SAMPLES_REQUIRED = 5;

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: '45%',
    padding: 28,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 16,
    borderBottom: '1px solid #E0E0E0'
  },
  title: { fontWeight: 700, fontSize: 18 },
  content: { flex: 1, overflowY: 'auto', marginBottom: 24, marginTop: 16 },
  sectionBox: {
    background: '#FAFAFA',
    padding: 16,
    marginBottom: 16,
    borderRadius: 8
  },
  labelSmall: { fontSize: 14, color: '#757575', marginBottom: 12 },
  labelMedium: { fontSize: 14, color: '#1E293B', fontWeight: 500, marginBottom: 8 },
  smallButton: {
    backgroundColor: '#00445D',
    color: '#fff',
    textTransform: 'none',
    borderRadius: 24,
    padding: '8px 24px',
    '&:hover': { backgroundColor: '#0d1b5e' },
    '&:disabled': {
      backgroundColor: '#ccc',
      color: '#666'
    }
  },
  footer: {
    borderTop: '1px solid #E0E0E0',
    paddingTop: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  textField: {
    marginBottom: 16,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    }
  },
  dropZone: {
    border: '2px dashed #C3DDEE',
    borderRadius: 8,
    padding: 24,
    textAlign: 'center',
    backgroundColor: '#F8FBFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#00445D',
      backgroundColor: '#E9F4FA'
    }
  },
  dropZoneActive: {
    borderColor: '#00445D',
    backgroundColor: '#E9F4FA'
  },
  dropZoneIcon: {
    fontSize: 40,
    color: '#00445D',
    marginBottom: 8
  },
  dropZoneText: {
    fontSize: 14,
    color: '#1E293B',
    marginBottom: 4
  },
  dropZoneSubtext: {
    fontSize: 12,
    color: '#6B7280'
  },
  fileList: {
    marginTop: 16,
    maxHeight: 200,
    overflowY: 'auto'
  },
  fileItem: {
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    marginBottom: 6,
    padding: '8px 12px',
    border: '1px solid #E5E7EB',
  },
  fileIcon: {
    color: '#00445D',
    marginRight: 8,
    fontSize: 20
  },
  fileName: {
    fontSize: 13,
    color: '#1E293B'
  },
  fileSize: {
    fontSize: 11,
    color: '#6B7280'
  },
  deleteButton: {
    color: '#9CA3AF',
    padding: 4,
    '&:hover': {
      color: '#D32F2F'
    }
  },
  requirementBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 16
  },
  requirementMet: {
    backgroundColor: '#E8F5E9',
    border: '1px solid #A5D6A7'
  },
  requirementNotMet: {
    backgroundColor: '#FFF3E0',
    border: '1px solid #FFCC80'
  },
  requirementIcon: {
    fontSize: 20
  },
  requirementText: {
    fontSize: 13,
    fontWeight: 500
  },
  hiddenInput: {
    display: 'none'
  },
  sampleCount: {
    fontSize: 13,
    color: '#6B7280'
  },
  // Loading overlay styles
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    padding: 32
  },
  loadingTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#1E293B',
    marginBottom: 8
  },
  loadingSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 24,
    textAlign: 'center'
  },
  progressContainer: {
    width: '100%',
    maxWidth: 400
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    marginBottom: 8,
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB'
  },
  stepItemActive: {
    backgroundColor: '#E9F4FA',
    border: '1px solid #00445D'
  },
  stepItemComplete: {
    backgroundColor: '#E8F5E9',
    border: '1px solid #4CAF50'
  },
  stepIcon: {
    marginRight: 12,
    fontSize: 24
  },
  stepText: {
    flex: 1
  },
  stepTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1E293B'
  },
  stepSubtitle: {
    fontSize: 12,
    color: '#6B7280'
  },
  progressBar: {
    marginTop: 24,
    borderRadius: 4,
    height: 8,
    backgroundColor: '#E5E7EB',
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#00445D',
      borderRadius: 4
    }
  },
  progressText: {
    marginTop: 8,
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center'
  },
  tipBox: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#FFF8E1',
    borderRadius: 8,
    border: '1px solid #FFE082'
  },
  tipText: {
    fontSize: 12,
    color: '#F57C00'
  }
}));

const STEPS = {
  IDLE: 'idle',
  CREATING_CATEGORY: 'creating_category',
  UPLOADING_SAMPLES: 'uploading_samples',
  PROCESSING: 'processing',
  COMPLETE: 'complete'
};

export default function AddCategoryModal({ 
  open, 
  onClose, 
  onCreate, 
  onSave, 
  initialData = null, 
  isEdit = false 
}) {
  const classes = useStyles();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [nameError, setNameError] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const [dragActive, setDragActive] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(STEPS.IDLE);
  const [progress, setProgress] = React.useState(0);
  const fileInputRef = React.useRef(null);

  const creating = currentStep !== STEPS.IDLE;

  React.useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setNameError(false);
      setFiles([]);
      setCurrentStep(STEPS.IDLE);
      setProgress(0);
    } else if (initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
    }
  }, [open, initialData]);

  const validateForm = () => {
    const validName = !!name.trim();
    setNameError(!validName);
    return validName;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!isEdit && files.length < MIN_SAMPLES_REQUIRED) return;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      sampleFiles: isEdit ? undefined : files,
      onStepChange: (step, progressValue) => {
        setCurrentStep(step);
        if (progressValue !== undefined) {
          setProgress(progressValue);
        }
      }
    };

    try {
      if (isEdit) {
        await onSave?.(payload);
      } else {
        setCurrentStep(STEPS.CREATING_CATEGORY);
        setProgress(0);
        await onCreate?.(payload);
      }
    } finally {
      setCurrentStep(STEPS.IDLE);
      setProgress(0);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    const fileArray = Array.from(newFiles);
    const existingNames = new Set(files.map(f => f.name));
    const uniqueNewFiles = fileArray.filter(f => !existingNames.has(f.name));
    setFiles(prev => [...prev, ...uniqueNewFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const hasEnoughSamples = files.length >= MIN_SAMPLES_REQUIRED;
  const isButtonDisabled = !name.trim() || (!isEdit && !hasEnoughSamples) || creating;

  const renderLoadingOverlay = () => {
    if (!creating) return null;

    const isCategoryStep = currentStep === STEPS.CREATING_CATEGORY;
    const isUploadStep = currentStep === STEPS.UPLOADING_SAMPLES || currentStep === STEPS.PROCESSING;
    const categoryComplete = isUploadStep;

    return (
      <Box className={classes.loadingOverlay}>
        <Typography className={classes.loadingTitle}>
          Creating Category
        </Typography>
        <Typography className={classes.loadingSubtitle}>
          Please wait while we set up your category and process the sample documents.
          <br />
          This may take a few seconds depending on the file sizes.
        </Typography>

        <Box className={classes.progressContainer}>
          {/* Step 1: Create Category */}
          <Box 
            className={`${classes.stepItem} ${isCategoryStep ? classes.stepItemActive : ''} ${categoryComplete ? classes.stepItemComplete : ''}`}
          >
            {isCategoryStep ? (
              <CircularProgress size={24} className={classes.stepIcon} style={{ color: '#00445D' }} />
            ) : categoryComplete ? (
              <CheckCircleIcon className={classes.stepIcon} style={{ color: '#4CAF50' }} />
            ) : (
              <CategoryIcon className={classes.stepIcon} style={{ color: '#9CA3AF' }} />
            )}
            <Box className={classes.stepText}>
              <Typography className={classes.stepTitle}>
                {categoryComplete ? 'Category Created' : 'Creating Category'}
              </Typography>
              <Typography className={classes.stepSubtitle}>
                {isCategoryStep ? 'Setting up category...' : categoryComplete ? `"${name}" created successfully` : 'Waiting...'}
              </Typography>
            </Box>
          </Box>

          {/* Step 2: Upload Samples */}
          <Box 
            className={`${classes.stepItem} ${isUploadStep ? classes.stepItemActive : ''}`}
          >
            {isUploadStep ? (
              <CircularProgress size={24} className={classes.stepIcon} style={{ color: '#00445D' }} />
            ) : (
              <DescriptionIcon className={classes.stepIcon} style={{ color: '#9CA3AF' }} />
            )}
            <Box className={classes.stepText}>
              <Typography className={classes.stepTitle}>
                {isUploadStep ? 'Uploading & Processing Samples' : 'Upload Samples'}
              </Typography>
              <Typography className={classes.stepSubtitle}>
                {isUploadStep 
                  ? `Processing ${files.length} documents...` 
                  : `${files.length} files ready to upload`
                }
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          {isUploadStep && (
            <>
              <LinearProgress 
                variant="indeterminate"
                className={classes.progressBar}
              />
              <Typography className={classes.progressText}>
                Extracting text from documents for classification training...
              </Typography>
            </>
          )}

          {/* Tip Box */}
          <Box className={classes.tipBox}>
            <Typography className={classes.tipText}>
              <strong>Tip:</strong> Text extraction uses OCR processing which may take a few minutes depending on the document size and complexity.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Drawer 
      anchor="right" 
      open={!!open} 
      onClose={creating ? undefined : onClose} 
      classes={{ paper: classes.drawerPaper }}
    >
      {/* Loading Overlay */}
      {renderLoadingOverlay()}

      <Box className={classes.header}>
        <Typography className={classes.title}>
          {isEdit ? 'Edit Category' : '+ Add Category'}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close" disabled={creating}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.content}>
        {/* Category Info Section */}
        <Box className={classes.sectionBox}>
          <Typography className={classes.labelSmall}>Category Info</Typography>
          
          <TextField
            label={<span>Category Name <span style={{ color: 'red' }}>*</span></span>}
            fullWidth
            variant="outlined"
            size="small"
            value={name}
            onChange={e => setName(e.target.value)}
            error={nameError}
            helperText={nameError ? 'Category name is required' : 'Unique name for this document category'}
            className={classes.textField}
            disabled={isEdit || creating}
          />

          <TextField
            label="Description"
            fullWidth
            variant="outlined"
            size="small"
            value={description}
            onChange={e => setDescription(e.target.value)}
            helperText="Optional description for this category"
            className={classes.textField}
            multiline
            rows={2}
            disabled={creating}
          />
        </Box>

        {/* Sample Documents Section - Only for Create mode */}
        {!isEdit && (
          <>
            <Box className={classes.sectionBox}>
              <Typography className={classes.labelMedium}>
                Sample Documents <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography style={{ fontSize: 12, color: '#6B7280', marginBottom: 16 }}>
                Upload at least {MIN_SAMPLES_REQUIRED} sample documents to train the classification system
              </Typography>

              {/* Requirement Status */}
              <Box 
                className={`${classes.requirementBox} ${hasEnoughSamples ? classes.requirementMet : classes.requirementNotMet}`}
              >
                {hasEnoughSamples ? (
                  <CheckCircleIcon className={classes.requirementIcon} style={{ color: '#4CAF50' }} />
                ) : (
                  <ErrorIcon className={classes.requirementIcon} style={{ color: '#FF9800' }} />
                )}
                <Typography 
                  className={classes.requirementText}
                  style={{ color: hasEnoughSamples ? '#2E7D32' : '#E65100' }}
                >
                  {hasEnoughSamples 
                    ? `${files.length} samples uploaded (minimum ${MIN_SAMPLES_REQUIRED} required)`
                    : `${files.length} of ${MIN_SAMPLES_REQUIRED} required samples uploaded`
                  }
                </Typography>
              </Box>

              {/* Drop Zone */}
              <input
                type="file"
                ref={fileInputRef}
                className={classes.hiddenInput}
                onChange={handleFileInputChange}
                multiple
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                disabled={creating}
              />
              
              <Box
                className={`${classes.dropZone} ${dragActive ? classes.dropZoneActive : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => !creating && fileInputRef.current?.click()}
                style={{ opacity: creating ? 0.6 : 1, cursor: creating ? 'not-allowed' : 'pointer' }}
              >
                <CloudUploadIcon className={classes.dropZoneIcon} />
                <Typography className={classes.dropZoneText}>
                  Drop files here or click to browse
                </Typography>
                <Typography className={classes.dropZoneSubtext}>
                  Supported: PDF, DOC, DOCX, TXT, PNG, JPG
                </Typography>
              </Box>

              {/* File List */}
              {files.length > 0 && (
                <Box className={classes.fileList}>
                  <Divider style={{ margin: '16px 0 12px 0' }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography className={classes.sampleCount}>
                      {files.length} file{files.length !== 1 ? 's' : ''} selected
                    </Typography>
                    {files.length > 0 && !creating && (
                      <Button 
                        size="small" 
                        onClick={() => setFiles([])}
                        style={{ textTransform: 'none', fontSize: 12 }}
                      >
                        Clear All
                      </Button>
                    )}
                  </Box>
                  <List disablePadding>
                    {files.map((file, index) => (
                      <ListItem key={`${file.name}-${index}`} className={classes.fileItem} disableGutters>
                        <InsertDriveFileIcon className={classes.fileIcon} />
                        <ListItemText
                          primary={<Typography className={classes.fileName}>{file.name}</Typography>}
                          secondary={<Typography className={classes.fileSize}>{formatFileSize(file.size)}</Typography>}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            className={classes.deleteButton}
                            onClick={() => removeFile(index)}
                            disabled={creating}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box className={classes.footer}>
        <Typography style={{ fontSize: 12, color: '#9CA3AF' }}>
          {!isEdit && !hasEnoughSamples && `Add ${MIN_SAMPLES_REQUIRED - files.length} more sample${MIN_SAMPLES_REQUIRED - files.length !== 1 ? 's' : ''} to continue`}
        </Typography>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          className={classes.smallButton}
        >
          {creating ? (
            <>
              <CircularProgress size={16} style={{ marginRight: 8, color: '#fff' }} />
              Creating...
            </>
          ) : (
            isEdit ? 'Save Changes' : 'Create Category'
          )}
        </Button>
      </Box>
    </Drawer>
  );
}
